// What moved between this machine and somewhere else.
//
//   aws s3 cp s3://bucket/actions_log/…/$TASK_ID.log.zst /tmp/apiserver.log
//   rsync -a ./dist/ user@host:/srv/app/
//
// DIRECTION IS THE READING. A download brings data in; an upload puts data
// somewhere it outlives the session and other people can read it. They look nearly
// identical on the command line — `cp A B` with the remote on either side — and
// conflating them turns "the agent published something" into "the agent looked at
// something".
//
// The target is the remote LOCATION with its variable parts placeheld, so repeated
// transfers under one prefix group instead of scattering across object keys.

import { positionalsOf, shellStatements } from "../../src/mine/statements.js";

const REMOTE_RE = /^(?:s3|gs|azblob|https?):\/\/|^[\w.-]+@[\w.-]+:|^[\w.-]+:\/(?!\/)/;

/** The prefix a remote location sits under, with ids and variables placeheld. */
export function locationOf(remote) {
  const s = String(remote || "").replace(/["']/g, "");
  // The bare `host:/path` form too. REMOTE_RE admitted it as remote while this
  // regex required a scheme or a user@, so `rsync ./dist/ backup-host:/srv/data/`
  // produced a null location and the whole op was dropped instead of surfaced.
  const m = /^((?:s3|gs|azblob|https?):\/\/[^/]+|[\w.-]+@[\w.-]+:|[\w.-]+:(?=\/))(.*)$/.exec(s);
  if (!m) return null;
  const all = (m[2] || "").split("/").filter(Boolean);
  // Drop a trailing OBJECT NAME. Object keys are unbounded, and keeping one made
  // every transferred file its own group — which is exactly what the prefix exists
  // to prevent. The s3 form only escaped this because its three-segment slice
  // happened to cut the filename off; a short remote path did not.
  const segments = /\.[A-Za-z0-9]{1,6}$/.test(all[all.length - 1] || "") ? all.slice(0, -1) : all;
  const parts = segments.map((p) => (
    /\$|\{/.test(p) ? "<var>" : /^[0-9a-f]{8,}$/i.test(p) ? "<id>" : /^\d+$/.test(p) ? "<n>" : p));
  // The PREFIX is the thing a reader knows.
  return `${m[1]}${parts.length ? `/${parts.slice(0, 3).join("/")}` : ""}${parts.length > 3 ? "/…" : ""}`;
}

export function extractTransferOps(turns) {
  const ops = [];
  for (const st of shellStatements(turns)) {
    const isAws = st.program === "aws" && /(?:^|\s)s3\b/.test(st.text);
    const isCopier = /^(?:scp|rsync|sftp|gsutil|azcopy)$/.test(st.program);
    if (!isAws && !isCopier) continue;

    const bare = positionalsOf(st.args, new Set(["--profile", "--region", "--exclude", "--include", "-e", "--rsh"]));
    const verb = (isAws ? bare[1] : (/^rsync$/.test(st.program) ? "sync" : "cp")) || "";
    if (isAws && !/^(?:cp|mv|sync|ls|rm|presign)$/.test(verb)) continue;

    const paths = bare.filter((t) => !/^(?:s3|cp|mv|sync|ls|rm|presign)$/.test(t));
    const remotes = paths.filter((p) => REMOTE_RE.test(p));
    const locals = paths.filter((p) => !REMOTE_RE.test(p));
    const location = locationOf(remotes[0]);
    if (!location) continue;

    // Which side the remote sits on decides the direction, and it is the whole
    // point of the lens.
    const lastIsRemote = REMOTE_RE.test(paths[paths.length - 1] || "");
    const kind = /^(?:ls|presign)$/.test(verb) ? "list"
      : verb === "rm" ? "delete"
      : remotes.length === 2 ? "remote-to-remote"
      : lastIsRemote ? "upload" : "download";

    const out = st.output;
    ops.push({
      key: st.key,
      location,
      // `split("://")[0]` returned the WHOLE location for the bare `host:/path` form,
      // which locationOf accepts. rsync and scp move that over ssh.
      scheme: location.includes("://") ? location.split("://")[0] : "ssh",
      verb: verb || st.program,
      kind,
      local: locals[0] || null,
      outcome: !out ? "unrecorded"
        : /\b(?:AccessDenied|NoSuchKey|NoSuchBucket|Forbidden|does not exist|fatal error)\b/i.test(out) ? "refused"
        : "completed",
      output: out,
      outputStatus: st.outputStatus,
      ok: st.ok,
      steps: [{ kind, cmd: st.text.replace(/\s+/g, " ").slice(0, 220) }],
      checks: [{ kind, tool: location, scope: [locals[0]].filter(Boolean),
        outcome: !out ? "unrecorded" : /AccessDenied|NoSuchKey|fatal error/i.test(out) ? "refused" : "completed" }],
    });
  }
  return ops;
}

export function describeMissing(op) {
  if (op.outcome === "refused") return "the store refused the transfer — nothing moved";
  switch (op.outputStatus) {
    case "backgrounded": return "ran in the background — whether the transfer completed never entered this trace";
    case "none": return "ran and captured no output, so whether anything moved is not recoverable";
    default: return null;
  }
}
